// pages/GuildFriendly.js (UI 개선 및 실제 Firebase 연동)
import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  orderBy,
  serverTimestamp,
  getDoc,
  onSnapshot
} from "firebase/firestore";
import { useFriendActions } from "./useFriendActions";

function GuildFriendly({ guildId, currentUser, guild }) {
  const [activeTab, setActiveTab] = useState("overview");
  const [guildFriends, setGuildFriends] = useState([]);
  const [friendlyMatches, setFriendlyMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [friendRequestInput, setFriendRequestInput] = useState("");
  const [allGuilds, setAllGuilds] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [msg, setMsg] = useState("");
  const [showResultModal, setShowResultModal] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [resultInput, setResultInput] = useState({ guild1: 0, guild2: 0 });

  const { friends, requestFriend, removeFriend, msg: friendMsg, loading: friendLoading } = useFriendActions(currentUser);

  // 권한 확인
  const isLeader = guild?.leaderId === currentUser?.id;
  const isSubLeader = currentUser?.guildTier === "부길드장";
  const canManageFriends = isLeader || isSubLeader;

  useEffect(() => {
    if (guildId) {
      loadFriendlyData();
      // 실시간 친선전 업데이트 구독
      const unsubscribe = subscribeToFriendlyMatches();
      return () => unsubscribe();
    }
  }, [guildId]);

  const subscribeToFriendlyMatches = () => {
    const matchesQuery = query(
      collection(db, "guildFriendlyMatches"),
      where("participatingGuilds", "array-contains", guildId),
      orderBy("createdAt", "desc")
    );
    
    return onSnapshot(matchesQuery, (snapshot) => {
      const matchesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      }));
      setFriendlyMatches(matchesData);
    });
  };

  const loadFriendlyData = async () => {
    try {
      setLoading(true);
      
      // 길드 친구 목록 로드
      await loadGuildFriends();
      
      // 친선전 기록 로드
      await loadFriendlyMatches();
      
      // 전체 길드 목록 로드 (친구 요청용)
      await loadAllGuilds();
      
      // 친구 요청 목록 로드
      await loadFriendRequests();

    } catch (error) {
      console.error("친선전 데이터 로드 실패:", error);
      setMsg("데이터를 불러오는데 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const loadGuildFriends = async () => {
    try {
      const friendsQuery = query(
        collection(db, "guildFriends"),
        where("guildId", "==", guildId),
        where("status", "==", "accepted")
      );
      const friendsSnap = await getDocs(friendsQuery);
      
      const friendsData = [];
      for (const friendDoc of friendsSnap.docs) {
        const friendData = friendDoc.data();
        const targetGuildId = friendData.targetGuildId;
        
        // 친구 길드 정보 가져오기
        const guildDoc = await getDoc(doc(db, "guilds", targetGuildId));
        if (guildDoc.exists()) {
          friendsData.push({
            id: friendDoc.id,
            ...friendData,
            guildInfo: { id: guildDoc.id, ...guildDoc.data() }
          });
        }
      }
      
      setGuildFriends(friendsData);
    } catch (error) {
      console.error("길드 친구 로드 실패:", error);
    }
  };

  const loadFriendlyMatches = async () => {
    try {
      const matchesQuery = query(
        collection(db, "guildFriendlyMatches"),
        where("participatingGuilds", "array-contains", guildId),
        orderBy("createdAt", "desc")
      );
      const matchesSnap = await getDocs(matchesQuery);
      
      const matchesData = matchesSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      }));
      
      setFriendlyMatches(matchesData);
    } catch (error) {
      console.error("친선전 기록 로드 실패:", error);
    }
  };

  const loadAllGuilds = async () => {
    try {
      const guildsSnap = await getDocs(collection(db, "guilds"));
      const guildsData = guildsSnap.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(g => g.id !== guildId); // 자신의 길드 제외
      
      setAllGuilds(guildsData);
    } catch (error) {
      console.error("전체 길드 목록 로드 실패:", error);
    }
  };

  const loadFriendRequests = async () => {
    try {
      const requestsQuery = query(
        collection(db, "guildFriendRequests"),
        where("targetGuildId", "==", guildId),
        where("status", "==", "pending")
      );
      const requestsSnap = await getDocs(requestsQuery);
      
      const requestsData = [];
      for (const requestDoc of requestsSnap.docs) {
        const requestData = requestDoc.data();
        
        // 요청한 길드 정보 가져오기
        const guildDoc = await getDoc(doc(db, "guilds", requestData.fromGuildId));
        if (guildDoc.exists()) {
          requestsData.push({
            id: requestDoc.id,
            ...requestData,
            fromGuildInfo: { id: guildDoc.id, ...guildDoc.data() }
          });
        }
      }
      
      setFriendRequests(requestsData);
    } catch (error) {
      console.error("친구 요청 목록 로드 실패:", error);
    }
  };

  const handleSendFriendRequest = async () => {
    if (!canManageFriends) {
      setMsg("길드장과 부길드장만 친구 요청을 보낼 수 있습니다.");
      return;
    }

    if (!friendRequestInput.trim()) {
      setMsg("길드명을 입력해주세요.");
      return;
    }

    try {
      // 길드명으로 검색
      const guildsQuery = query(
        collection(db, "guilds"),
        where("name", "==", friendRequestInput.trim())
      );
      const guildsSnap = await getDocs(guildsQuery);
      
      if (guildsSnap.empty) {
        setMsg("해당 이름의 길드를 찾을 수 없습니다.");
        return;
      }

      const targetGuild = guildsSnap.docs[0];
      const targetGuildData = targetGuild.data();

      // 이미 친구인지 확인
      const existingFriendQuery = query(
        collection(db, "guildFriends"),
        where("guildId", "==", guildId),
        where("targetGuildId", "==", targetGuild.id)
      );
      const existingFriendSnap = await getDocs(existingFriendQuery);
      
      if (!existingFriendSnap.empty) {
        setMsg("이미 친구로 등록된 길드입니다.");
        return;
      }

      // 이미 요청이 있는지 확인
      const existingRequestQuery = query(
        collection(db, "guildFriendRequests"),
        where("fromGuildId", "==", guildId),
        where("targetGuildId", "==", targetGuild.id),
        where("status", "==", "pending")
      );
      const existingRequestSnap = await getDocs(existingRequestQuery);
      
      if (!existingRequestSnap.empty) {
        setMsg("이미 친구 요청을 보낸 길드입니다.");
        return;
      }

      // 친구 요청 생성
      await addDoc(collection(db, "guildFriendRequests"), {
        fromGuildId: guildId,
        fromGuildName: guild.name,
        targetGuildId: targetGuild.id,
        targetGuildName: targetGuildData.name,
        status: "pending",
        createdAt: serverTimestamp(),
        createdBy: currentUser.id,
        requestId: `${guildId}_${targetGuild.id}`, // 고유 요청 ID
        message: `${guild.name} 길드에서 친구 요청을 보냈습니다!`
      });

      // 상대 길드장에게 알림 전송
      if (targetGuildData.leaderId) {
        try {
          await addDoc(collection(db, "notifications"), {
            userId: targetGuildData.leaderId,
            type: "guildFriendRequest",
            title: "새로운 길드 친구 요청",
            message: `${guild.name} 길드에서 친구 요청을 보냈습니다.`,
            data: {
              fromGuildId: guildId,
              fromGuildName: guild.name,
              requestId: `${guildId}_${targetGuild.id}`
            },
            isRead: false,
            createdAt: serverTimestamp()
          });
        } catch (error) {
          console.error("알림 전송 실패:", error);
          // 알림 전송 실패해도 친구 요청은 계속 진행
        }
      }

      setMsg("친구 요청이 전송되었습니다. 상대방 길드장에게 알림이 전송되었습니다.");
      setFriendRequestInput("");
    } catch (error) {
      console.error("친구 요청 전송 실패:", error);
      setMsg("친구 요청 전송에 실패했습니다.");
    }
  };

  const handleAcceptFriendRequest = async (requestId, fromGuildId, fromGuildName) => {
    if (!canManageFriends) {
      setMsg("길드장과 부길드장만 친구 요청을 승인할 수 있습니다.");
      return;
    }

    try {
      // 양방향 친구 관계 생성
      await addDoc(collection(db, "guildFriends"), {
        guildId: guildId,
        guildName: guild.name,
        targetGuildId: fromGuildId,
        targetGuildName: fromGuildName,
        status: "accepted",
        createdAt: serverTimestamp(),
        friendshipId: `${guildId}_${fromGuildId}` // 양방향 관계를 연결하는 ID
      });

      await addDoc(collection(db, "guildFriends"), {
        guildId: fromGuildId,
        guildName: fromGuildName,
        targetGuildId: guildId,
        targetGuildName: guild.name,
        status: "accepted",
        createdAt: serverTimestamp(),
        friendshipId: `${guildId}_${fromGuildId}` // 같은 friendshipId로 연결
      });

      // 친구 요청 상태 업데이트
      await updateDoc(doc(db, "guildFriendRequests", requestId), {
        status: "accepted",
        acceptedAt: serverTimestamp(),
        acceptedBy: currentUser.id,
        friendshipId: `${guildId}_${fromGuildId}`
      });

      setMsg("친구 요청을 수락했습니다. 이제 양방향 친구 관계가 설정되었습니다!");
      
      // 데이터 새로고침
      await Promise.all([
        loadGuildFriends(),
        loadFriendRequests()
      ]);
      
    } catch (error) {
      console.error("친구 요청 수락 실패:", error);
      setMsg(`친구 요청 수락에 실패했습니다: ${error.message}`);
    }
  };

  const handleRejectFriendRequest = async (requestId) => {
    if (!canManageFriends) {
      setMsg("길드장과 부길드장만 친구 요청을 거절할 수 있습니다.");
      return;
    }

    try {
      await updateDoc(doc(db, "guildFriendRequests", requestId), {
        status: "rejected",
        rejectedAt: serverTimestamp(),
        rejectedBy: currentUser.id
      });

      setMsg("친구 요청을 거절했습니다.");
      // 실시간 데이터는 자동으로 업데이트됩니다
    } catch (error) {
      console.error("친구 요청 거절 실패:", error);
      setMsg("친구 요청 거절에 실패했습니다.");
    }
  };

  // 친구 관계 삭제
  const handleRemoveFriend = async (friendGuildId) => {
    if (!canManageFriends) {
      setMsg("길드장과 부길드장만 친구를 제거할 수 있습니다.");
      return;
    }

    try {
      // 양방향 친구 관계 모두 삭제
      const friendshipId = `${guildId}_${friendGuildId}`;
      
      // 현재 길드의 친구 관계 삭제
      const currentFriendQuery = query(
        collection(db, "guildFriends"),
        where("guildId", "==", guildId),
        where("targetGuildId", "==", friendGuildId)
      );
      const currentFriendSnap = await getDocs(currentFriendQuery);
      
      // 상대방 길드의 친구 관계 삭제
      const targetFriendQuery = query(
        collection(db, "guildFriends"),
        where("guildId", "==", friendGuildId),
        where("targetGuildId", "==", guildId)
      );
      const targetFriendSnap = await getDocs(targetFriendQuery);

      // 삭제 실행
      const deletePromises = [];
      
      currentFriendSnap.docs.forEach(doc => {
        deletePromises.push(deleteDoc(doc.ref));
      });
      
      targetFriendSnap.docs.forEach(doc => {
        deletePromises.push(deleteDoc(doc.ref));
      });

      await Promise.all(deletePromises);

      setMsg("길드 친구 관계가 삭제되었습니다.");
      // 실시간 데이터는 자동으로 업데이트됩니다
      
    } catch (error) {
      console.error("친구 관계 삭제 실패:", error);
      setMsg("친구 관계 삭제에 실패했습니다.");
    }
  };

  const handleChallengeFriend = async (friendGuildId, friendGuildName) => {
    if (!canManageFriends) {
      setMsg("길드장과 부길드장만 친선전을 신청할 수 있습니다.");
      return;
    }

    try {
      await addDoc(collection(db, "guildFriendlyMatches"), {
        guild1Id: guildId,
        guild1Name: guild.name,
        guild2Id: friendGuildId,
        guild2Name: friendGuildName,
        participatingGuilds: [guildId, friendGuildId],
        sport: guild.sport,
        status: "pending", // pending, accepted, rejected, completed
        type: "friendly",
        createdAt: serverTimestamp(),
        createdBy: currentUser.id,
        matches: [], // 실제 경기 결과
        score: { guild1: 0, guild2: 0 },
        challengeMessage: `${guild.name} 길드에서 친선전을 신청합니다!`
      });

      setMsg("친선전 도전이 전송되었습니다.");
      loadFriendlyData(); // 새로고침
    } catch (error) {
      console.error("친선전 도전 실패:", error);
      setMsg("친선전 도전에 실패했습니다.");
    }
  };

  // 친선전 수락
  const handleAcceptChallenge = async (matchId) => {
    try {
      await updateDoc(doc(db, "guildFriendlyMatches", matchId), {
        status: "accepted",
        acceptedAt: serverTimestamp(),
        acceptedBy: currentUser.id
      });
      setMsg("친선전을 수락했습니다!");
    } catch (error) {
      console.error("친선전 수락 실패:", error);
      setMsg("친선전 수락에 실패했습니다.");
    }
  };

  // 친선전 거절
  const handleRejectChallenge = async (matchId) => {
    try {
      await updateDoc(doc(db, "guildFriendlyMatches", matchId), {
        status: "rejected",
        rejectedAt: serverTimestamp(),
        rejectedBy: currentUser.id
      });
      setMsg("친선전을 거절했습니다.");
    } catch (error) {
      console.error("친선전 거절 실패:", error);
      setMsg("친선전 거절에 실패했습니다.");
    }
  };

  // 친선전 취소
  const handleCancelChallenge = async (matchId) => {
    if (!canManageFriends) {
      setMsg("길드장과 부길드장만 친선전을 취소할 수 있습니다.");
      return;
    }

    try {
      // 친선전 내역을 완전히 삭제
      await deleteDoc(doc(db, "guildFriendlyMatches", matchId));
      setMsg("친선전 신청이 취소되었습니다.");
    } catch (error) {
      console.error("친선전 취소 실패:", error);
      setMsg("친선전 취소에 실패했습니다.");
    }
  };

  // 친선전 결과 입력 모달 열기
  const openResultModal = (match) => {
    setSelectedMatch(match);
    setResultInput({ guild1: 0, guild2: 0 });
    setShowResultModal(true);
  };

  // 친선전 결과 입력
  const handleSubmitResult = async () => {
    if (!selectedMatch) return;

    try {
      const isGuild1 = selectedMatch.guild1Id === guildId;
      const finalScore = isGuild1 ? 
        { guild1: resultInput.guild1, guild2: resultInput.guild2 } :
        { guild1: resultInput.guild2, guild2: resultInput.guild1 };

      await updateDoc(doc(db, "guildFriendlyMatches", selectedMatch.id), {
        status: "completed",
        completedAt: serverTimestamp(),
        completedBy: currentUser.id,
        score: finalScore,
        finalResult: {
          winner: finalScore.guild1 > finalScore.guild2 ? selectedMatch.guild1Id : 
                 finalScore.guild1 < finalScore.guild2 ? selectedMatch.guild2Id : "draw",
          score: finalScore
        }
      });

      setMsg("친선전 결과가 입력되었습니다!");
      setShowResultModal(false);
      setSelectedMatch(null);
    } catch (error) {
      console.error("결과 입력 실패:", error);
      setMsg("결과 입력에 실패했습니다.");
    }
  };

  const getSportEmoji = (sport) => {
    const emojis = {
      "탁구": "🏓",
      "배드민턴": "🏸", 
      "테니스": "🎾",
      "볼링": "🎳",
      "축구": "⚽",
      "농구": "🏀"
    };
    return emojis[sport] || "🏃";
  };

  const getMatchResultColor = (result) => {
    switch(result) {
      case "승리": return "#4caf50";
      case "패배": return "#f44336";
      case "무승부": return "#ff9800";
      default: return "#9e9e9e";
    }
  };

  const getMatchIcon = (result) => {
    switch(result) {
      case "승리": return "🏆";
      case "패배": return "😔";
      case "무승부": return "🤝";
      default: return "⚡";
    }
  };

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.loadingSpinner}></div>
        <div style={styles.loadingText}>친선전 정보를 불러오는 중...</div>
      </div>
    );
  }

  return (
    <div style={styles.friendlyContainer}>
      {/* 헤더 */}
      <div style={styles.friendlyHeader}>
        <h2 style={styles.friendlyTitle}>🤝 길드 친선전</h2>
        <div style={styles.friendlySubtitle}>
          다른 길드와의 우정과 실력 향상을 위한 친선 경기
        </div>
      </div>

      {/* 통계 개요 */}
      <div style={styles.overviewStats} className="glass-morphism">
        <div style={styles.statItem}>
          <div style={styles.statIcon}>👫</div>
          <div style={styles.statValue}>{guildFriends.length}</div>
          <div style={styles.statLabel}>길드 친구</div>
        </div>
        
        <div style={styles.statItem}>
          <div style={styles.statIcon}>🏆</div>
          <div style={styles.statValue}>{friendlyMatches.length}</div>
          <div style={styles.statLabel}>전체 친선전</div>
        </div>
        
        <div style={styles.statItem}>
          <div style={styles.statIcon}>📩</div>
          <div style={styles.statValue}>{friendRequests.length}</div>
          <div style={styles.statLabel}>대기 요청</div>
        </div>
        
        <div style={styles.statItem}>
          <div style={styles.statIcon}>⚔️</div>
          <div style={styles.statValue}>
            {friendlyMatches.filter(m => m.status === 'completed').length}
          </div>
          <div style={styles.statLabel}>완료된 경기</div>
        </div>

        <div style={styles.statItem}>
          <div style={styles.statIcon}>⏳</div>
          <div style={styles.statValue}>
            {friendlyMatches.filter(m => m.status === 'pending').length}
          </div>
          <div style={styles.statLabel}>대기중인 경기</div>
        </div>

        <div style={styles.statItem}>
          <div style={styles.statIcon}>🎯</div>
          <div style={styles.statValue}>
            {friendlyMatches.filter(m => m.status === 'accepted').length}
          </div>
          <div style={styles.statLabel}>진행중인 경기</div>
        </div>
      </div>

      {/* 탭 네비게이션 */}
      <div style={styles.tabNavigation} className="glass-morphism">
        {[
          { key: "overview", label: "📊 개요", icon: "📊" },
          { key: "friends", label: "👫 길드 친구", icon: "👫" },
          { key: "matches", label: "🏆 친선경기", icon: "🏆" }
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              ...styles.tabButton,
              ...(activeTab === tab.key ? styles.activeTabButton : {})
            }}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* 탭 콘텐츠 */}
      <div style={styles.tabContent}>
        {/* 개요 탭 */}
        {activeTab === "overview" && (
          <div style={styles.overviewContent}>
            {/* 친구 요청 알림 */}
            {friendRequests.length > 0 && (
              <div style={styles.requestsAlert} className="glass-morphism">
                <div style={styles.alertIcon}>🔔</div>
                <div style={styles.alertContent}>
                  <div style={styles.alertTitle}>새로운 친구 요청이 있습니다!</div>
                  <div style={styles.alertSubtext}>
                    {friendRequests.length}개의 길드에서 친구 요청을 보냈습니다.
                  </div>
                </div>
                <button 
                  onClick={() => setActiveTab("friends")}
                  style={styles.alertButton}
                >
                  확인하기
                </button>
              </div>
            )}

            {/* 최근 활동 */}
            <div style={styles.recentActivity} className="glass-morphism">
              <h3 style={styles.sectionTitle}>📈 최근 친선전 활동</h3>
              
              <div style={styles.activityList}>
                {friendlyMatches.slice(0, 3).map((match, index) => {
                  const isGuild1 = match.guild1Id === guildId;
                  const opponentName = isGuild1 ? match.guild2Name : match.guild1Name;
                  const result = match.status === 'completed' ? 
                    (match.score.guild1 > match.score.guild2 ? 
                      (isGuild1 ? "승리" : "패배") : 
                      (isGuild1 ? "패배" : "승리")) : 
                    "진행중";

                  return (
                    <div key={match.id} style={styles.activityItem}>
                      <div style={styles.activityIcon}>
                        {getMatchIcon(result)}
                      </div>
                      <div style={styles.activityText}>
                        <div style={styles.activityTitle}>
                          vs {opponentName} - {result}
                        </div>
                        <div style={styles.activityDate}>
                          {match.createdAt.toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  );
                })}
                
                {friendlyMatches.length === 0 && (
                  <div style={styles.emptyActivity}>
                    아직 친선경기 기록이 없습니다.
                  </div>
                )}
              </div>
            </div>

            {/* 추천 길드 */}
            <div style={styles.recommendedGuilds} className="glass-morphism">
              <h3 style={styles.sectionTitle}>💡 친구 추천 길드</h3>
              
              <div style={styles.guildGrid}>
                {allGuilds
                  .filter(g => g.sport === guild.sport) // 같은 운동
                  .filter(g => !guildFriends.some(f => f.targetGuildId === g.id)) // 이미 친구인 길드 제외
                  .slice(0, 4)
                  .map(recommendGuild => (
                    <div key={recommendGuild.id} style={styles.recommendedGuildCard}>
                      <div style={styles.guildAvatar}>
                        {recommendGuild.logoUrl ? (
                          <img src={recommendGuild.logoUrl} alt="길드 로고" style={styles.guildImage} />
                        ) : (
                          <div style={styles.guildDefaultAvatar}>🏰</div>
                        )}
                      </div>
                      <div style={styles.guildInfo}>
                        <div style={styles.guildName}>{recommendGuild.name}</div>
                        <div style={styles.guildSport}>
                          {getSportEmoji(recommendGuild.sport)} {recommendGuild.sport}
                        </div>
                        <div style={styles.guildMembers}>
                          👥 {recommendGuild.memberCount || 0}명
                        </div>
                        <div style={styles.guildRegion}>
                          📍 {recommendGuild.region}
                        </div>
                      </div>
                      {canManageFriends && (
                        <button 
                          onClick={() => {
                            setFriendRequestInput(recommendGuild.name);
                            setActiveTab("friends");
                          }}
                          style={styles.recommendButton}
                          disabled={friendLoading}
                        >
                          친구 추가
                        </button>
                      )}
                      
                      {/* 이미 친구인 경우 친선전 신청 버튼 표시 */}
                      {guildFriends.some(f => f.targetGuildId === recommendGuild.id) && canManageFriends && (
                        <button 
                          onClick={() => handleChallengeFriend(recommendGuild.id, recommendGuild.name)}
                          style={styles.directChallengeButton}
                          disabled={friendLoading}
                        >
                          🤝 친선전 신청
                        </button>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}

        {/* 길드 친구 탭 */}
        {activeTab === "friends" && (
          <div style={styles.friendsContent}>
            {/* 친구 추가 */}
            {canManageFriends && (
              <div style={styles.addFriendSection} className="glass-morphism">
                <h3 style={styles.sectionTitle}>➕ 새로운 길드 친구 추가</h3>
                <div style={styles.addFriendForm}>
                  <input
                    type="text"
                    value={friendRequestInput}
                    onChange={(e) => setFriendRequestInput(e.target.value)}
                    placeholder="길드명을 정확히 입력하세요"
                    style={styles.friendInput}
                  />
                  <button 
                    onClick={handleSendFriendRequest}
                    style={styles.sendRequestButton}
                    disabled={friendLoading}
                  >
                    친구 요청
                  </button>
                </div>
                <div style={styles.addFriendTip}>
                  💡 팁: 정확한 길드명을 입력해야 합니다.
                </div>
              </div>
            )}

            {/* 대기 중인 요청 */}
            {friendRequests.length > 0 && canManageFriends && (
              <div style={styles.pendingRequests} className="glass-morphism">
                <h3 style={styles.sectionTitle}>📩 받은 친구 요청</h3>
                <div style={styles.requestsList}>
                  {friendRequests.map(request => (
                    <div key={request.id} style={styles.requestItem}>
                      <div style={styles.requestInfo}>
                        <div style={styles.requestGuildName}>
                          {request.fromGuildInfo?.name || request.fromGuildName}
                        </div>
                        <div style={styles.requestMeta}>
                          <span>{getSportEmoji(request.fromGuildInfo?.sport)} {request.fromGuildInfo?.sport}</span>
                          <span>📍 {request.fromGuildInfo?.region}</span>
                        </div>
                        <div style={styles.requestDate}>
                          {request.createdAt?.toDate ? 
                            request.createdAt.toDate().toLocaleDateString() : 
                            "날짜 미상"}
                        </div>
                      </div>
                      <div style={styles.requestActions}>
                        <button 
                          onClick={() => handleAcceptFriendRequest(
                            request.id, 
                            request.fromGuildId, 
                            request.fromGuildInfo?.name || request.fromGuildName
                          )}
                          style={styles.acceptButton}
                          disabled={friendLoading}
                        >
                          수락
                        </button>
                        <button 
                          onClick={() => handleRejectFriendRequest(request.id)}
                          style={styles.rejectButton}
                          disabled={friendLoading}
                        >
                          거절
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 친구 목록 */}
            <div style={styles.friendsList} className="glass-morphism">
              <h3 style={styles.sectionTitle}>👫 길드 친구 목록</h3>
              
              {guildFriends.length > 0 ? (
                <div style={styles.friendsGrid}>
                  {guildFriends.map(friend => (
                    <div key={friend.id} style={styles.friendCard}>
                      <div style={styles.friendHeader}>
                        <div style={styles.friendAvatar}>
                          {friend.guildInfo?.logoUrl ? (
                            <img src={friend.guildInfo.logoUrl} alt="길드 로고" style={styles.friendImage} />
                          ) : (
                            <div style={styles.friendDefaultAvatar}>🏰</div>
                          )}
                        </div>
                        <div style={styles.friendInfo}>
                          <div style={styles.friendName}>
                            {friend.guildInfo?.name || friend.targetGuildName}
                          </div>
                          <div style={styles.friendSport}>
                            {getSportEmoji(friend.guildInfo?.sport)} {friend.guildInfo?.sport}
                          </div>
                          <div style={styles.friendMembers}>
                            👥 {friend.guildInfo?.memberCount || 0}명
                          </div>
                          <div style={styles.friendRegion}>
                            📍 {friend.guildInfo?.region}
                          </div>
                        </div>
                      </div>
                      
                      <div style={styles.friendStats}>
                        <div style={styles.friendshipSince}>
                          친구된 날: {friend.createdAt?.toDate ? 
                            friend.createdAt.toDate().toLocaleDateString() : 
                            "날짜 미상"}
                        </div>
                      </div>

                      <div style={styles.friendActions}>
                        {canManageFriends ? (
                          <div style={styles.friendActionButtons}>
                            <button 
                              onClick={() => handleChallengeFriend(
                                friend.targetGuildId, 
                                friend.guildInfo?.name || friend.targetGuildName
                              )}
                              style={styles.friendlyMatchButton}
                              disabled={friendLoading}
                            >
                              🤝 친선전 신청
                            </button>
                            <button 
                              onClick={() => handleRemoveFriend(friend.targetGuildId)}
                              style={styles.removeFriendButton}
                              disabled={friendLoading}
                            >
                              ❌ 친구 제거
                            </button>
                          </div>
                        ) : (
                          <div style={styles.noPermissionText}>
                            친구 관리와 친선전 신청은 길드장/부길드장만 가능합니다
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={styles.emptyFriends}>
                  <div style={styles.emptyIcon}>👫</div>
                  <div style={styles.emptyTitle}>아직 길드 친구가 없습니다</div>
                  <div style={styles.emptySubtext}>
                    다른 길드에 친구 요청을 보내보세요!
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 친선경기 탭 */}
        {activeTab === "matches" && (
          <div style={styles.matchesContent}>
            <div style={styles.matchesHeader}>
              <h3 style={styles.sectionTitle}>🏆 친선경기 기록</h3>
              <div style={styles.matchStats}>
                완료된 경기: {friendlyMatches.filter(m => m.status === 'completed').length}개
              </div>
            </div>

            {friendlyMatches.length > 0 ? (
              <div style={styles.matchesList}>
                {friendlyMatches.map(match => {
                  const isGuild1 = match.guild1Id === guildId;
                  const opponentName = isGuild1 ? match.guild2Name : match.guild1Name;
                  const isChallenger = match.createdBy === currentUser.id;
                  
                  let result = "진행중";
                  if (match.status === 'completed') {
                    if (match.score.guild1 > match.score.guild2) {
                      result = isGuild1 ? "승리" : "패배";
                    } else if (match.score.guild1 < match.score.guild2) {
                      result = isGuild1 ? "패배" : "승리";
                    } else {
                      result = "무승부";
                    }
                  } else if (match.status === 'pending') {
                    result = "대기중";
                  } else if (match.status === 'accepted') {
                    result = "진행중";
                  } else if (match.status === 'rejected') {
                    result = "거절됨";
                  } else if (match.status === 'cancelled') {
                    result = "취소됨";
                  }

                  return (
                    <div key={match.id} style={styles.matchCard} className="glass-morphism">
                      <div style={styles.matchHeader}>
                        <div style={styles.matchResult}>
                          <span style={styles.matchIcon}>
                            {getMatchIcon(result)}
                          </span>
                          <span 
                            style={{
                              ...styles.matchResultText,
                              color: getMatchResultColor(result)
                            }}
                          >
                            {result}
                          </span>
                        </div>
                        <div style={styles.matchDate}>
                          {match.createdAt.toLocaleDateString()}
                        </div>
                      </div>

                      <div style={styles.matchDetails}>
                        <div style={styles.matchOpponent}>
                          <div style={styles.vsLabel}>VS</div>
                          <div style={styles.opponentName}>{opponentName}</div>
                        </div>
                        
                        {match.status === 'completed' && (
                          <div style={styles.matchScore}>
                            <div style={styles.scoreLabel}>스코어</div>
                            <div style={styles.scoreValue}>
                              {match.score.guild1} : {match.score.guild2}
                            </div>
                          </div>
                        )}
                      </div>

                      <div style={styles.matchMeta}>
                        <span>상태: {match.status === 'pending' ? '대기중' : 
                                     match.status === 'accepted' ? '수락됨' :
                                     match.status === 'completed' ? '완료' : 
                                     match.status === 'rejected' ? '거절됨' : '기타'}</span>
                        <span>종목: {getSportEmoji(match.sport)} {match.sport}</span>
                      </div>

                      {/* 친선전 액션 버튼들 */}
                      <div style={styles.matchActions}>
                        {match.status === 'pending' && !isChallenger && canManageFriends && (
                          <div style={styles.challengeActions}>
                            <button 
                              onClick={() => handleAcceptChallenge(match.id)}
                              style={styles.acceptChallengeButton}
                            >
                              ✅ 친선전 수락
                            </button>
                            <button 
                              onClick={() => handleRejectChallenge(match.id)}
                              style={styles.rejectChallengeButton}
                            >
                              ❌ 친선전 거절
                            </button>
                          </div>
                        )}

                        {match.status === 'pending' && isChallenger && canManageFriends && (
                          <button 
                            onClick={() => handleCancelChallenge(match.id)}
                            style={styles.cancelChallengeButton}
                          >
                            🚫 친선전 취소
                          </button>
                        )}

                        {match.status === 'accepted' && canManageFriends && (
                          <button 
                            onClick={() => openResultModal(match)}
                            style={styles.submitResultButton}
                          >
                            📝 결과 입력
                          </button>
                        )}

                        {match.status === 'pending' && isChallenger && !canManageFriends && (
                          <div style={styles.pendingStatus}>
                            상대방의 응답을 기다리는 중...
                          </div>
                        )}

                        {match.status === 'rejected' && (
                          <div style={styles.rejectedStatus}>
                            상대방이 친선전을 거절했습니다.
                          </div>
                        )}

                        {match.status === 'cancelled' && (
                          <div style={styles.cancelledStatus}>
                            친선전 신청이 취소되었습니다.
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={styles.emptyMatches} className="glass-morphism">
                <div style={styles.emptyIcon}>🏆</div>
                <div style={styles.emptyTitle}>아직 친선경기 기록이 없습니다</div>
                <div style={styles.emptySubtext}>
                  길드 친구들과 친선경기를 시작해보세요!
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 메시지 표시 */}
      {msg && (
        <div style={styles.messageBar}>
          {msg}
        </div>
      )}

      {/* 결과 입력 모달 */}
      {showResultModal && selectedMatch && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <h3 style={styles.modalTitle}>🏆 친선전 결과 입력</h3>
            <div style={styles.modalBody}>
              <div style={styles.matchInfo}>
                <div style={styles.matchTeams}>
                  <span>{selectedMatch.guild1Name}</span>
                  <span style={styles.vs}>VS</span>
                  <span>{selectedMatch.guild2Name}</span>
                </div>
                <div style={styles.sportInfo}>
                  {getSportEmoji(selectedMatch.sport)} {selectedMatch.sport}
                </div>
              </div>
              
              <div style={styles.scoreInput}>
                <div style={styles.scoreInputGroup}>
                  <label>{selectedMatch.guild1Name}</label>
                  <input
                    type="number"
                    min="0"
                    value={resultInput.guild1}
                    onChange={(e) => setResultInput(prev => ({...prev, guild1: parseInt(e.target.value) || 0}))}
                    style={styles.scoreInputField}
                  />
                </div>
                <div style={styles.scoreInputGroup}>
                  <label>{selectedMatch.guild2Name}</label>
                  <input
                    type="number"
                    min="0"
                    value={resultInput.guild2}
                    onChange={(e) => setResultInput(prev => ({...prev, guild2: parseInt(e.target.value) || 0}))}
                    style={styles.scoreInputField}
                  />
                </div>
              </div>
            </div>
            
            <div style={styles.modalActions}>
              <button 
                onClick={() => setShowResultModal(false)}
                style={styles.cancelButton}
              >
                취소
              </button>
              <button 
                onClick={handleSubmitResult}
                style={styles.submitButton}
              >
                결과 입력
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  friendlyContainer: {
    color: "#1f2937"
  },
  loading: {
    textAlign: "center",
    padding: 60,
    color: "#6b7280",
    fontSize: 18
  },
  loadingSpinner: {
    width: 40,
    height: 40,
    border: "3px solid rgba(107,114,128,0.3)",
    borderTop: "3px solid #667eea",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
    margin: "0 auto 20px"
  },
  loadingText: {
    marginTop: 15,
    fontSize: 16
  },
  
  friendlyHeader: {
    textAlign: "center",
    marginBottom: 32,
    color: "#1f2937"
  },
  friendlyTitle: {
    fontSize: 32,
    fontWeight: 900,
    margin: "0 0 8px 0"
  },
  friendlySubtitle: {
    fontSize: 16,
    opacity: 0.8,
    fontWeight: 500
  },
  
  overviewStats: {
    borderRadius: 20,
    padding: 24,
    marginBottom: 24,
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: 20,
    background: "rgba(255,255,255,0.95)",
    border: "1px solid rgba(0,0,0,0.1)"
  },
  statItem: {
    textAlign: "center"
  },
  statIcon: {
    fontSize: 32,
    marginBottom: 8
  },
  statValue: {
    fontSize: 24,
    fontWeight: 900,
    color: "#1f2937",
    lineHeight: 1
  },
  statLabel: {
    fontSize: 14,
    opacity: 0.8,
    marginTop: 4,
    fontWeight: 600
  },
  
  tabNavigation: {
    borderRadius: 16,
    padding: 8,
    marginBottom: 24,
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    background: "rgba(255,255,255,0.95)",
    border: "1px solid rgba(0,0,0,0.1)"
  },
  tabButton: {
    flex: 1,
    minWidth: 150,
    padding: "14px 20px",
    background: "transparent",
    color: "#6b7280",
    border: "none",
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    transition: "all 0.3s"
  },
  activeTabButton: {
    background: "rgba(102,126,234,0.1)",
    color: "#667eea",
    boxShadow: "0 2px 8px rgba(102,126,234,0.2)"
  },
  
  tabContent: {
    minHeight: 400
  },
  
  overviewContent: {
    display: "flex",
    flexDirection: "column",
    gap: 24
  },
  
  requestsAlert: {
    borderRadius: 16,
    padding: 20,
    display: "flex",
    alignItems: "center",
    gap: 16,
    backgroundColor: "rgba(251,191,36,0.1)",
    border: "1px solid rgba(251,191,36,0.3)",
    color: "#1f2937"
  },
  alertIcon: {
    fontSize: 24,
    flexShrink: 0
  },
  alertContent: {
    flex: 1
  },
  alertTitle: {
    fontSize: 16,
    fontWeight: 700,
    marginBottom: 4
  },
  alertSubtext: {
    fontSize: 14,
    opacity: 0.8
  },
  alertButton: {
    background: "#fbbf24",
    color: "#1f2937",
    border: "none",
    borderRadius: 8,
    padding: "10px 16px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer"
  },
  
  recentActivity: {
    borderRadius: 16,
    padding: 24,
    background: "rgba(255,255,255,0.95)",
    border: "1px solid rgba(0,0,0,0.1)"
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 800,
    marginBottom: 20,
    color: "#1f2937"
  },
  activityList: {
    display: "flex",
    flexDirection: "column",
    gap: 12
  },
  activityItem: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: 12,
    background: "rgba(0,0,0,0.05)",
    borderRadius: 10
  },
  activityIcon: {
    fontSize: 20,
    flexShrink: 0
  },
  activityText: {
    flex: 1
  },
  activityTitle: {
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 2
  },
  activityDate: {
    fontSize: 12,
    opacity: 0.7
  },
  emptyActivity: {
    textAlign: "center",
    padding: 40,
    opacity: 0.7,
    fontSize: 16
  },
  
  recommendedGuilds: {
    borderRadius: 16,
    padding: 24,
    background: "rgba(255,255,255,0.95)",
    border: "1px solid rgba(0,0,0,0.1)"
  },
  guildGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
    gap: 16
  },
  recommendedGuildCard: {
    padding: 16,
    background: "rgba(0,0,0,0.05)",
    borderRadius: 12,
    textAlign: "center",
    border: "1px solid rgba(0,0,0,0.1)"
  },
  guildAvatar: {
    marginBottom: 12,
    display: "flex",
    justifyContent: "center"
  },
  guildImage: {
    width: 48,
    height: 48,
    borderRadius: "50%",
    objectFit: "cover"
  },
  guildDefaultAvatar: {
    width: 48,
    height: 48,
    borderRadius: "50%",
    backgroundColor: "rgba(102,126,234,0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 20,
    color: "#667eea"
  },
  guildInfo: {
    marginBottom: 12
  },
  guildName: {
    fontSize: 16,
    fontWeight: 700,
    marginBottom: 4
  },
  guildSport: {
    fontSize: 14,
    color: "#667eea",
    marginBottom: 2
  },
  guildMembers: {
    fontSize: 12,
    opacity: 0.8,
    marginBottom: 2
  },
  guildRegion: {
    fontSize: 12,
    opacity: 0.8
  },
  challengeButton: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "8px 16px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.3s",
    marginBottom: 8
  },
  recommendButton: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "8px 16px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.3s",
    width: "100%",
    marginBottom: 8
  },
  directChallengeButton: {
    background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "8px 16px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.3s",
    width: "100%"
  },
  
  friendsContent: {
    display: "flex",
    flexDirection: "column",
    gap: 24
  },
  
  addFriendSection: {
    borderRadius: 16,
    padding: 24,
    background: "rgba(255,255,255,0.95)",
    border: "1px solid rgba(0,0,0,0.1)"
  },
  addFriendForm: {
    display: "flex",
    gap: 12,
    alignItems: "center",
    marginBottom: 8
  },
  friendInput: {
    flex: 1,
    padding: "12px 16px",
    border: "2px solid rgba(0,0,0,0.1)",
    borderRadius: 10,
    background: "#fff",
    color: "#1f2937",
    fontSize: 16
  },
  sendRequestButton: {
    background: "#2196f3",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    padding: "12px 20px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer"
  },
  addFriendTip: {
    fontSize: 12,
    color: "#6b7280",
    fontStyle: "italic"
  },
  
  pendingRequests: {
    borderRadius: 16,
    padding: 24,
    background: "rgba(255,255,255,0.95)",
    border: "1px solid rgba(0,0,0,0.1)"
  },
  requestsList: {
    display: "flex",
    flexDirection: "column",
    gap: 12
  },
  requestItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 16,
    background: "rgba(0,0,0,0.05)",
    borderRadius: 10,
    border: "1px solid rgba(0,0,0,0.1)"
  },
  requestInfo: {
    flex: 1
  },
  requestGuildName: {
    fontSize: 16,
    fontWeight: 600,
    marginBottom: 4
  },
  requestMeta: {
    fontSize: 12,
    color: "#6b7280",
    marginBottom: 4,
    display: "flex",
    gap: 12
  },
  requestDate: {
    fontSize: 12,
    opacity: 0.7
  },
  requestActions: {
    display: "flex",
    gap: 8
  },
  acceptButton: {
    background: "#4caf50",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    padding: "8px 16px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer"
  },
  rejectButton: {
    background: "#f44336",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    padding: "8px 16px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer"
  },
  
  friendsList: {
    borderRadius: 16,
    padding: 24,
    background: "rgba(255,255,255,0.95)",
    border: "1px solid rgba(0,0,0,0.1)"
  },
  friendsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
    gap: 20
  },
  friendCard: {
    padding: 20,
    background: "rgba(0,0,0,0.05)",
    borderRadius: 12,
    border: "1px solid rgba(0,0,0,0.1)"
  },
  friendHeader: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    marginBottom: 16
  },
  friendAvatar: {
    flexShrink: 0
  },
  friendImage: {
    width: 56,
    height: 56,
    borderRadius: "50%",
    objectFit: "cover"
  },
  friendDefaultAvatar: {
    width: 56,
    height: 56,
    borderRadius: "50%",
    backgroundColor: "rgba(102,126,234,0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 24,
    color: "#667eea"
  },
  friendInfo: {
    flex: 1
  },
  friendName: {
    fontSize: 18,
    fontWeight: 700,
    marginBottom: 4
  },
  friendSport: {
    fontSize: 14,
    color: "#667eea",
    marginBottom: 2
  },
  friendStats: {
    marginBottom: 16,
    fontSize: 13,
    opacity: 0.8
  },
  friendshipSince: {
    marginBottom: 4
  },
  friendActions: {
    textAlign: "center",
    marginTop: 12
  },
  friendlyMatchButton: {
    background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    padding: "12px 20px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.3s",
    width: "100%",
    boxShadow: "0 4px 12px rgba(16,185,129,0.3)"
  },
  noPermissionText: {
    fontSize: 12,
    color: "#6b7280",
    fontStyle: "italic",
    textAlign: "center",
    padding: "8px"
  },
  friendActionButtons: {
    display: "flex",
    gap: 10
  },
  removeFriendButton: {
    background: "#f44336",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "8px 16px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.3s",
    boxShadow: "0 4px 12px rgba(244,67,54,0.3)"
  },
  
  emptyFriends: {
    textAlign: "center",
    padding: 60
  },
  emptyIcon: {
    fontSize: 48,
    marginBottom: 16,
    opacity: 0.5
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: 700,
    marginBottom: 8
  },
  emptySubtext: {
    fontSize: 16,
    opacity: 0.7,
    lineHeight: 1.5
  },
  
  matchesContent: {
    display: "flex",
    flexDirection: "column",
    gap: 24
  },
  matchesHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 16
  },
  matchStats: {
    fontSize: 16,
    fontWeight: 600,
    color: "#667eea"
  },
  matchesList: {
    display: "flex",
    flexDirection: "column",
    gap: 16
  },
  matchCard: {
    borderRadius: 16,
    padding: 20,
    background: "rgba(255,255,255,0.95)",
    border: "1px solid rgba(0,0,0,0.1)"
  },
  matchHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16
  },
  matchResult: {
    display: "flex",
    alignItems: "center",
    gap: 8
  },
  matchIcon: {
    fontSize: 20
  },
  matchResultText: {
    fontSize: 16,
    fontWeight: 700
  },
  matchDate: {
    fontSize: 14,
    opacity: 0.7
  },
  matchDetails: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 16,
    marginBottom: 12
  },
  matchOpponent: {
    display: "flex",
    alignItems: "center",
    gap: 12
  },
  vsLabel: {
    fontSize: 12,
    opacity: 0.6,
    fontWeight: 600
  },
  opponentName: {
    fontSize: 16,
    fontWeight: 600
  },
  matchScore: {
    textAlign: "right"
  },
  scoreLabel: {
    fontSize: 12,
    opacity: 0.6,
    marginBottom: 2
  },
  scoreValue: {
    fontSize: 16,
    fontWeight: 700,
    color: "#667eea"
  },
  matchMeta: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 12,
    opacity: 0.7,
    flexWrap: "wrap",
    gap: 12
  },
  matchActions: {
    display: "flex",
    justifyContent: "space-around",
    marginTop: 16,
    gap: 10
  },
  challengeActions: {
    display: "flex",
    gap: 10
  },
  acceptChallengeButton: {
    background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "8px 16px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.3s",
    boxShadow: "0 4px 12px rgba(16,185,129,0.3)"
  },
  rejectChallengeButton: {
    background: "#f44336",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "8px 16px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.3s",
    boxShadow: "0 4px 12px rgba(244,67,54,0.3)"
  },
  submitResultButton: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    padding: "12px 20px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.3s",
    boxShadow: "0 4px 12px rgba(102,126,234,0.3)"
  },
  pendingStatus: {
    fontSize: 14,
    color: "#6b7280",
    textAlign: "center",
    padding: "10px 0",
    background: "rgba(107,114,128,0.1)",
    borderRadius: 8,
    border: "1px solid rgba(107,114,128,0.3)"
  },
  rejectedStatus: {
    fontSize: 14,
    color: "#f44336",
    textAlign: "center",
    padding: "10px 0",
    background: "rgba(244,67,54,0.1)",
    borderRadius: 8,
    border: "1px solid rgba(244,67,54,0.3)"
  },
  cancelledStatus: {
    fontSize: 14,
    color: "#9e9e9e",
    textAlign: "center",
    padding: "10px 0",
    background: "rgba(158,158,158,0.1)",
    borderRadius: 8,
    border: "1px solid rgba(158,158,158,0.3)"
  },
  
  cancelChallengeButton: {
    background: "#9e9e9e",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    padding: "12px 20px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.3s",
    boxShadow: "0 4px 12px rgba(158,158,158,0.3)"
  },
  
  emptyMatches: {
    borderRadius: 16,
    padding: 60,
    textAlign: "center",
    background: "rgba(255,255,255,0.95)",
    border: "1px solid rgba(0,0,0,0.1)"
  },
  
  messageBar: {
    background: "rgba(102,126,234,0.1)",
    color: "#667eea",
    padding: 16,
    borderRadius: 12,
    marginTop: 16,
    textAlign: "center",
    fontWeight: 600,
    border: "1px solid rgba(102,126,234,0.3)"
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0,0,0,0.6)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000
  },
  modalContent: {
    background: "#fff",
    borderRadius: 16,
    padding: 30,
    width: "90%",
    maxWidth: 500,
    boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
    position: "relative"
  },
  modalTitle: {
    fontSize: 24,
    fontWeight: 800,
    marginBottom: 20,
    textAlign: "center"
  },
  modalBody: {
    marginBottom: 20
  },
  matchInfo: {
    textAlign: "center",
    marginBottom: 15
  },
  matchTeams: {
    fontSize: 20,
    fontWeight: 700,
    marginBottom: 5
  },
  vs: {
    fontSize: 20,
    fontWeight: 700,
    margin: "0 10px"
  },
  sportInfo: {
    fontSize: 16,
    color: "#667eea",
    fontWeight: 600
  },
  scoreInput: {
    display: "flex",
    justifyContent: "space-around",
    gap: 20
  },
  scoreInputGroup: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center"
  },
  scoreInputField: {
    width: "100%",
    padding: "12px 16px",
    border: "2px solid rgba(0,0,0,0.1)",
    borderRadius: 10,
    background: "#fff",
    color: "#1f2937",
    fontSize: 18,
    textAlign: "center",
    fontWeight: 600
  },
  modalActions: {
    display: "flex",
    justifyContent: "space-around",
    gap: 10
  },
  cancelButton: {
    background: "#e0e0e0",
    color: "#1f2937",
    border: "none",
    borderRadius: 10,
    padding: "12px 20px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.3s",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
  },
  submitButton: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    padding: "12px 20px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.3s",
    boxShadow: "0 4px 12px rgba(102,126,234,0.3)"
  }
};

export default GuildFriendly;